import { useState } from 'react';

import { FilterOptions, Sort } from '@vimeo/comments-ui';
import { PatchAlbumVideoCommentFields } from '@vimeo/gctl-api/fetchers/album/video/comment';
import { PatchVideoCommentFields } from '@vimeo/gctl-api/fetchers/video/comment';
import { usePatchAlbumVideoComment } from '@vimeo/gctl-api/hooks/useAlbumVideoComment';
import { usePatchVideoComment } from '@vimeo/gctl-api/hooks/useVideoComment';
import { usePatchVideoPrivateComment } from '@vimeo/gctl-api/hooks/useVideoPrivateComment';
import { Comment } from '@vimeo/gctl-api/models/components/schemas/comment/Comment';
import { PrivateComments } from '@vimeo/gctl-api/models/components/schemas/private-comments/PrivateComments';
import { Video as VideoData } from '@vimeo/gctl-api/models/components/schemas/video/Video';
import { translate } from '@vimeo/i18n-runtime';
import { getPasswordHashFromCookie } from '@vimeo/use-unlocked-video';

import { Position } from '../../AnnotationsOverlay/types';
import { AnalyticsPropsType } from '../../CommentsCommon/types';
import { bpResolveComment, bpSaveEditComment } from '../../utils/analytics';
import { EDIT_PUBLIC_COMMENT_FIELDS } from '../../utils/constants';
import {
  checkForMention,
  getReviewPasswordHashFromCookie,
  getShowcasePasswordHashFromCookie,
  getVideoIdFromClipRequestId,
  idFromUri,
} from '../../utils/helper';
import { useVideoData } from '../use-video-data';
import { useViewer } from '../use-viewer';
import { usePrivateComments, usePublicComments } from './use-get-comments';
import { useVideoPrivacy } from './use-video-privacy';

/**
 * A customized hook to handle editing Public and Private comments data depending on privacy
 */
export function useEditComment(
  clipRequestId: string,
  isPublic: boolean,
  sort?: Sort,
  analyticsProps?: AnalyticsPropsType,
  filterOptions?: FilterOptions,
  showcaseId?: number,
  reviewId?: string,
) {
  const { videoData } = useVideoData(clipRequestId, showcaseId, reviewId);
  const useEditCommentsHook = isPublic ? useEditPublicComment : useEditPrivateComment;
  return useEditCommentsHook(
    clipRequestId,
    videoData,
    sort,
    analyticsProps,
    filterOptions,
    showcaseId,
    reviewId,
  );
}

/**
 * A hook to handle editing a Private comment
 */
export function useEditPrivateComment(
  clipRequestId: string,
  videoData?: VideoData,
  sort?: Sort,
  analyticsProps?: AnalyticsPropsType,
  filterOptions?: FilterOptions,
  showcaseId?: number,
  reviewId?: string,
) {
  const viewer = useViewer();
  const videoId = getVideoIdFromClipRequestId(clipRequestId);
  const { privacy } = useVideoPrivacy(clipRequestId, undefined, reviewId);
  const [editPrivateComment, { loading, error: patchCommentError }] = usePatchVideoPrivateComment();
  const { revalidateComments } = usePrivateComments(
    clipRequestId,
    sort,
    filterOptions,
    undefined,
    reviewId,
  );
  const [activeUri, setActiveUri] = useState<string | null>(null);
  const password = getReviewPasswordHashFromCookie(reviewId);

  const resolveComment = async (uri: string) => {
    patchComment(uri, { status: 'closed' });
  };

  const unresolveComment = async (uri: string) => {
    patchComment(uri, { status: 'open' });
  };

  const updateCommentPosition = async (uri: string, position: Position) => {
    patchComment(uri, { coordinates: position });
  };

  const updateText = async (uri: string, text: string) => {
    patchComment(uri, { richtext: text });
  };

  // We can use this hook to edit other properties of comment such as status, timeCode, etc.

  const patchComment = async (
    uri: string,
    newCommentsData: { richtext?: string; status?: 'open' | 'closed'; coordinates?: Position },
  ) => {
    setActiveUri(uri);
    await editPrivateComment({
      where: {
        videoId: clipRequestId as unknown as number,
        commentId: idFromUri(uri) as unknown as number,
      },
      variables: newCommentsData,
      select: [
        'metadata.connections.replies.total',
        'text',
        'richtext',
        'uri',
        'metadata.connections.user',
        'createdTime',
        'replies.text',
        'replies.richtext',
        'replies.createdTime',
        'replies.metadata.connections.user',
        'replies.uri',
        'replies.userName',
        'metadata',
        'timeCode',
        'userName',
      ],
      query: { reviewId, password },
    });
    revalidateData(uri, newCommentsData);
  };

  const revalidateData = (uri: string, newCommentsData: Partial<PrivateComments>) => {
    if (!loading && !patchCommentError) {
      let editedComment: PrivateComments | null = null;
      revalidateComments((data) => {
        const filteredData = data
          ?.filter((page) => !!page)
          .map((set) => {
            return {
              ...set,
              data: set.data?.map((comment: PrivateComments) => {
                if (comment.uri === uri) {
                  editedComment = { ...comment, ...newCommentsData };
                  return editedComment;
                }
                return comment;
              }),
            };
          });
        return filteredData;
      });

      if (analyticsProps) {
        const parsedRichtext =
          newCommentsData?.richtext ||
          (editedComment as unknown as PrivateComments)?.richtext ||
          '';
        const isMention = checkForMention(parsedRichtext);
        let analytics = {
          ...analyticsProps,
          commentContextFields: {
            comment_id: idFromUri(uri) as number,
            is_reply: false,
            comment_length: newCommentsData?.richtext?.length ?? null,
            is_mention: isMention,
          },
        };
        if (newCommentsData?.richtext || newCommentsData?.coordinates || '') {
          bpSaveEditComment(
            videoId,
            privacy?.view,
            false,
            analytics,
            viewer,
            !!(editedComment as unknown as PrivateComments)?.timeCode,
            videoData?.uploader?.link,
            newCommentsData?.coordinates ? 'annotation' : 'text',
          );
        } else if (newCommentsData?.status || '') {
          bpResolveComment(
            newCommentsData.status === 'closed',
            videoId,
            privacy?.view,
            analytics,
            viewer,
            !!(editedComment as unknown as PrivateComments)?.timeCode,
            videoData?.uploader?.link,
          );
        }
      }
    }
  };

  return {
    updateCommentText: updateText,
    resolveComment,
    unresolveComment,
    updateCommentPosition,
    editCommentLoading: loading,
    editCommentError: !!patchCommentError
      ? { errorMessage: translate('Unable to edit comment'), errorCommentId: activeUri }
      : null,
  };
}

/**
 * A hook to handle editing a Public comment
 */
export function useEditPublicComment(
  clipRequestId: string,
  videoData?: VideoData,
  sort?: Sort,
  analyticsProps?: AnalyticsPropsType,
  filterOptions?: FilterOptions,
  showcaseId?: number,
) {
  const viewer = useViewer();
  const videoId = getVideoIdFromClipRequestId(clipRequestId);
  const { privacy } = useVideoPrivacy(clipRequestId, showcaseId);
  const [editPublicComment, editState] = usePatchVideoComment();
  const [editAlbumPublicComment, albumEditState] = usePatchAlbumVideoComment();
  const { loading, error: patchCommentError } = showcaseId ? albumEditState : editState;
  const { revalidateComments } = usePublicComments(clipRequestId, sort, filterOptions, showcaseId);
  const [activeUri, setActiveUri] = useState<string | null>(null);

  const password = getPasswordHashFromCookie(clipRequestId);
  const updateText = async (uri: string, text: string) => {
    const commentText = { richtext: text };

    setActiveUri(uri);
    await editPublicComment({
      where: {
        videoId: clipRequestId as unknown as number,
        commentId: idFromUri(uri) as unknown as number,
      },
      variables: commentText,
      select: EDIT_PUBLIC_COMMENT_FIELDS as PatchVideoCommentFields,
      ...(password ? { query: { password } } : {}),
    });
    revalidateData(uri, { text });
  };

  const updateAlbumText = async (uri: string, text: string) => {
    if (!showcaseId) return;

    const commentText = { richtext: text };
    const showcasePassword = getShowcasePasswordHashFromCookie(showcaseId);

    setActiveUri(uri);
    await editAlbumPublicComment({
      where: {
        albumId: showcaseId,
        videoId: clipRequestId as unknown as number,
        commentId: idFromUri(uri) as unknown as number,
      },
      variables: commentText,
      select: EDIT_PUBLIC_COMMENT_FIELDS as PatchAlbumVideoCommentFields,
      ...(showcasePassword ? { query: { password: showcasePassword } } : {}),
    });
    revalidateData(uri, { text });
  };

  const revalidateData = (uri: string, newCommentsData: Partial<Comment>) => {
    if (!loading && !patchCommentError) {
      if (analyticsProps) {
        const isMention = checkForMention(newCommentsData?.text);
        let analytics = {
          ...analyticsProps,
          commentContextFields: {
            comment_id: idFromUri(uri) as number,
            is_reply: false,
            comment_length: newCommentsData?.text?.length,
            is_mention: isMention,
          },
        };
        bpSaveEditComment(videoId, privacy?.view, true, analytics, viewer);
      }

      revalidateComments((data) => {
        const filteredData = data
          ?.filter((page) => !!page)
          .map((set) => {
            return {
              ...set,
              data: set.data?.map((comment: Comment) => {
                if (comment.uri === uri) {
                  return { ...comment, ...newCommentsData };
                }
                return comment;
              }),
            };
          });
        return filteredData;
      });
    }
  };

  return {
    updateCommentText: showcaseId ? updateAlbumText : updateText,
    resolveComment: () => null,
    unresolveComment: () => null,
    editCommentLoading: loading,
    editCommentError: !!patchCommentError
      ? { errorMessage: translate('Unable to edit comment'), errorCommentId: activeUri }
      : null,
  };
}
